import { FC, useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { parseISO, format } from 'date-fns';
import { getAuthorFullName } from '@/helpers/helpers';
import BookCardImg from '../book-card-img/BookCardImg';
import RatingStars from './rating-stars/RatingStars';
import BookSeries from '../book-series/BookSeries';
import WebtoonQr from '@/components/screens/book-detail/webtoon-qr/WebtoonQr';
import LitresWidget from '@/components/ui/litres-widget/LitresWidget';
import LitresAudioPlayer from '../litres-audio-player/LitresAudioPlayer';
import BtnReadOnline from '../btn-read-online/BtnReadOnline';
import BtnsReviewQuote from '../btns-review-quote/BtnsReviewQuote';
import BookRatingJsonMarkup from '../book-rating-json-markup/BookRatingJsonMarkup';
import SocialsBar from '../socials-bar/SocialsBar';
import { BOOK_BTN_LFROM, BOOK_WIDGET_LFROM, DEV_ABSOLUTE_URL } from '@/helpers/variables';
import { IBookDetailData, IBookDetailGenre, IBookFiles } from '@/interfaces/bookDetail.interface';
import styles from './BookInfo.module.scss';
import LitresRating from './litresRating/LitresRating';

interface IBookInfo {
  book: IBookDetailData;
  reviewsCount: number;
  hideLitresWidgets: boolean;
}

const BookInfo: FC<IBookInfo> = ({ book, reviewsCount, hideLitresWidgets }) => {
  const { rating } = book;
  const { type, title, genres, external_id, id } = book?.book;
  const { first_name, middle_name, last_name } = book?.author;

  const authorFullName = getAuthorFullName(first_name, middle_name, last_name);
  const authorFullAlias = `/author/id${book.author.id}`;
  const createdAt = book.created_at;

  const [formatDownload, setFormatDownload] = useState(book.files.length > 0 ? book.files[0].type : false);
  const [isFormatDropdownActive, setIsFormatDropdownActive] = useState(false);

  const refFormatMenu = useRef<HTMLDivElement>(null);

  const getBookInfoRows = () => {
    return (
      <>
        <div className={styles.bookInfo__row}>
          <b>Автор: </b>
          <Link prefetch={false} href={authorFullAlias}>
            {authorFullName}
          </Link>
        </div>
        <div className={styles.bookInfo__row}>
          <b>Название: </b>
          {title}
        </div>

        <div className={styles.bookInfo__row}>
          <b>Жанр: </b>
          {genres.map((genre: IBookDetailGenre, i: number) => (
            <span key={genre.id}>
              {' '}
              <Link prefetch={false} className={styles.bookInfo__rowLink} href={`/genre/${genre.alias}`}>
                {genre.title}
              </Link>
              {i < genres.length - 1 ? ',' : ''}
            </span>
          ))}
        </div>

        {!!book.reader && book.reader.alias.length > 0 && (
          <div className={styles.bookInfo__row}>
            <b>Чтец: </b>
            <Link prefetch={false} className={styles.bookInfo__rowLink} href={`/reader/${book.reader.alias}`}>
              {getAuthorFullName(book.reader.first_name, book.reader.middle_name, book.reader.last_name)}
            </Link>
          </div>
        )}

        {!!book.translator && book.translator.alias.length > 0 && (
          <div className={styles.bookInfo__row}>
            <b>Переводчик: </b>
            {getAuthorFullName(book.translator.first_name, book.translator.middle_name, book.translator.last_name)}
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (refFormatMenu.current && refFormatMenu.current.contains(event.target as HTMLElement)) {
        return;
      }
      setIsFormatDropdownActive(false);
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  return (
    <>
      {!!genres.length && book.rating > 0 && <BookRatingJsonMarkup book={book} authorFullName={authorFullName} />}
      <h1 className={styles.bookInfoTitle}>{`${authorFullName} - ${title}`}</h1>

      <div className={styles.bookInfo}>
        <div className={styles.bookInfo__imageWrap}>
          <div className={styles.bookInfo__img}>
            <BookCardImg type={book.book.type} freeVersion={book.book.allow_full_free} coverId={external_id} title={title} bookId={id} retelling={!!book.retelling_text} draft={book.draft} />
          </div>

          <div className={styles.bookInfo__imgDesс}>
            <div className={`${styles.bookInfo__rows} ${styles.bookInfo__rows_mob}`}>{getBookInfoRows()}</div>
            <RatingStars rating={rating} title={title} id={book.id} />
            {(book.litres_rating !== 0 || book.livelib_rating !== 0) && <LitresRating litresRating={book.litres_rating} liveLibRating={book.litres_rating} />}
            <BtnsReviewQuote reviewLink="#bookReviewForm" />
            <SocialsBar bookLink={`/lib/id${book.id}`} />
          </div>
        </div>

        <div className={styles.bookInfo__desc}>
          <div className={`${styles.bookInfo__rows}`}>{getBookInfoRows()}</div>
          <div className={styles.bookDescription}>{book.book.description !== null && book.book.description.length > 1 && <p className={styles.bookDescription__txt}>{book.book.description}</p>}</div>
          {book.book.webtoon && book.book.external_id && <WebtoonQr externalId={book.book.external_id} />}
          {book.series && book.series[0]?.other_series_books.length > 0 && <BookSeries series={book.series} />}
          {!book.book.webtoon && (
            <div className={styles.bookControls}>
              {type === 'book' && !book.hide_litres_data && (
                <div className={styles.bookControls__download}>
                  <BtnReadOnline alias={`/lib/id${book.id}/read`} />
                </div>
              )}

              {type !== 'pdf' && !!formatDownload && (
                <div className={styles.bookControls__formats}>
                  <div className={styles.bookControls__formatsSelectWrap} ref={refFormatMenu}>
                    <div className={`${styles.bookControls__formatsSelect} ${styles.btn_yellow}`} onClick={() => setIsFormatDropdownActive(!isFormatDropdownActive)}>
                      {formatDownload}
                    </div>
                    <div className={!!isFormatDropdownActive ? `${styles.bookControls__formatsSelectDrop} ${styles.bookControls__formatsSelectDrop_active}` : `${styles.bookControls__formatsSelectDrop}`}>
                      {book.files.map((file: IBookFiles) => (
                        <span
                          key={`${file.type}`}
                          className={styles.bookControls__format}
                          onClick={() => {
                            setFormatDownload(file.type);
                            setIsFormatDropdownActive(false);
                          }}
                        >
                          {file.type}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Link prefetch={false} target="_blank" href={`${DEV_ABSOLUTE_URL}/trial/${formatDownload === 'mp3' ? 'mp3' : 'txt'}/${external_id}.${formatDownload}`} className={`${styles.bookControls__formatsBtn} ${styles.btn_yellow}`}>
                    Скачать
                  </Link>
                </div>
              )}
            </div>
          )}
          {book.book.webtoon && (
            <div className={`${styles.bookControls__download} ${styles.bookControls__download_mbNone}`}>
              <BtnReadOnline name="Купить и скачать" alias={`https://www.litres.ru/pages/biblio_book/?art=${book.book.external_id}&lfrom=${BOOK_BTN_LFROM}`} />
            </div>
          )}

          {type === 'audio_book' && (
            <div className={styles.bookInfo__audioPlayer}>
              <div className={styles.litresWidget__title}>Слушать онлайн</div>
              <LitresAudioPlayer externalId={external_id} />
            </div>
          )}
          {!hideLitresWidgets && (
            <div className={styles.litresWidget}>
              <div id="widget">
                <LitresWidget bookTitle={title} lfrom={Number(BOOK_WIDGET_LFROM)} authorName={authorFullName} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.b_biblio_book_line}>
        <span className={`${styles.added} ${styles.bolder_r}`}>
          <span className={styles.posted}>
            Добавленo: <time dateTime={createdAt}>{format(parseISO(createdAt!), 'dd.MM.yyyy')}</time> <time dateTime={createdAt}>{format(parseISO(createdAt!), 'HH:MM')}</time>
          </span>
        </span>

        <span className={styles.rating}>Рейтинг: {rating > 0 ? rating : 'Будь первым, кто оценит книгу'} </span>

        <span className={styles.comments}>
          <Link prefetch={false} href={`#bookReviewForm`}>
            Комментариев {!!reviewsCount ? reviewsCount : '0'} шт.
          </Link>
        </span>
      </div>
    </>
  );
};

export default BookInfo;
